import { InformationCard } from "./InformationCard";
import { GlobalView } from "./ViewFrame";
import styled from "styled-components";

interface Branding {
  backgroundColor?: string;
  backgroundImageUrl?: string;
  backgroundImageMode?: "full" | "banner";
  logoUrl?: string;
  fontFamily?: string | null;
}

interface StateViewProps {
  branding?: Branding;
}

const BannerArea = styled.div<{ $backgroundImageUrl: string }>`
  width: 100%;
  height: 300px;
  background-image: url(${props => props.$backgroundImageUrl});
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  position: absolute;
  top: 0;
  left: 0;
  z-index: 0;
  box-shadow: 0px 8px 24px 0px rgba(0, 0, 0, 0.15), 0px 2px 8px 0px rgba(0, 0, 0, 0.1);
`;

const BannerContent = styled.div`
  position: relative;
  z-index: 1;
  padding-top: 200px;
  display: flex;
  justify-content: center;
  align-items: flex-start;
`;

export const LoadingForm = ({ branding }: StateViewProps = {}) => {
  return (
    <GlobalView
      style={{
        ...(branding?.backgroundColor && {
          backgroundColor: branding.backgroundColor
        }),
        ...(branding?.backgroundImageUrl && {
          backgroundImage: `url(${branding.backgroundImageUrl})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat"
        }),
        ...(branding?.fontFamily && {
          // @ts-ignore - Setting CSS variables for font
          "--typography-font-text": `'${branding.fontFamily}', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Helvetica Neue', Arial, sans-serif`,
          "--typography-font-heading": `'${branding.fontFamily}', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Helvetica Neue', Arial, sans-serif`,
          fontFamily: `'${branding.fontFamily}', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Helvetica Neue', Arial, sans-serif`
        })
      }}
    >
      <BannerContent style={{ paddingTop: 0 }}>
        <InformationCard
          iconName="checkCircle"
          title="We're loading your form"
          description="Please hold tight for a moment while we get everything ready for you."
          loader
          logoUrl={branding?.logoUrl}
        />
      </BannerContent>
    </GlobalView>
  );
};

export const FormSubmitted = ({ branding }: StateViewProps = {}) => {
  const isBannerMode = branding?.backgroundImageMode === "banner" && branding?.backgroundImageUrl;
  const isFullMode =
    (branding?.backgroundImageMode === "full" || !branding?.backgroundImageMode) &&
    branding?.backgroundImageUrl;

  return (
    <GlobalView
      style={{
        ...(branding?.backgroundColor && {
          backgroundColor: branding.backgroundColor
        }),
        ...(isFullMode && {
          backgroundImage: `url(${branding?.backgroundImageUrl})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat"
        }),
        ...(isBannerMode && {
          position: "relative"
        }),
        ...(branding?.fontFamily && {
          // @ts-ignore - Setting CSS variables for font
          "--typography-font-text": `'${branding.fontFamily}', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Helvetica Neue', Arial, sans-serif`,
          "--typography-font-heading": `'${branding.fontFamily}', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Helvetica Neue', Arial, sans-serif`,
          fontFamily: `'${branding.fontFamily}', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Helvetica Neue', Arial, sans-serif`
        })
      }}
    >
      {isBannerMode && branding?.backgroundImageUrl && (
        <BannerArea $backgroundImageUrl={branding.backgroundImageUrl} />
      )}
      <BannerContent style={!isBannerMode ? { paddingTop: 0 } : {}}>
        <InformationCard
          iconName="checkCircle"
          title={`Thank you for your submission!`}
          description="Your form has been successfully submitted. You can now close the tab."
          logoUrl={branding?.logoUrl}
        />
      </BannerContent>
    </GlobalView>
  );
};

export const AlreadySubmitted = ({ branding }: StateViewProps = {}) => {
  const isBannerMode = branding?.backgroundImageMode === "banner" && branding?.backgroundImageUrl;
  const isFullMode =
    (branding?.backgroundImageMode === "full" || !branding?.backgroundImageMode) &&
    branding?.backgroundImageUrl;

  return (
    <GlobalView
      style={{
        ...(branding?.backgroundColor && {
          backgroundColor: branding.backgroundColor
        }),
        ...(isFullMode && {
          backgroundImage: `url(${branding?.backgroundImageUrl})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat"
        }),
        ...(isBannerMode && {
          position: "relative"
        }),
        ...(branding?.fontFamily && {
          // @ts-ignore - Setting CSS variables for font
          "--typography-font-text": `'${branding.fontFamily}', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Helvetica Neue', Arial, sans-serif`,
          "--typography-font-heading": `'${branding.fontFamily}', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Helvetica Neue', Arial, sans-serif`,
          fontFamily: `'${branding.fontFamily}', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Helvetica Neue', Arial, sans-serif`
        })
      }}
    >
      {isBannerMode && branding?.backgroundImageUrl && (
        <BannerArea $backgroundImageUrl={branding.backgroundImageUrl} />
      )}
      <BannerContent style={!isBannerMode ? { paddingTop: 0 } : {}}>
        <InformationCard
          iconName="info"
          title={`This form has already been submitted.`}
          description="It looks like this form has been completed and can't be filled out again. "
          logoUrl={branding?.logoUrl}
        />
      </BannerContent>
    </GlobalView>
  );
};

export const ErrorForm = ({ branding }: StateViewProps = {}) => {
  return (
    <GlobalView
      style={{
        ...(branding?.backgroundColor && {
          backgroundColor: branding.backgroundColor
        }),
        ...(branding?.backgroundImageUrl && {
          backgroundImage: `url(${branding.backgroundImageUrl})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat"
        }),
        ...(branding?.fontFamily && {
          // @ts-ignore - Setting CSS variables for font
          "--typography-font-text": `'${branding.fontFamily}', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Helvetica Neue', Arial, sans-serif`,
          "--typography-font-heading": `'${branding.fontFamily}', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Helvetica Neue', Arial, sans-serif`,
          fontFamily: `'${branding.fontFamily}', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Helvetica Neue', Arial, sans-serif`
        })
      }}
    >
      <BannerContent style={{ paddingTop: 0 }}>
        <InformationCard
          state="error"
          iconName="warning"
          title="Oops, we couldn't load the form."
          description="The form you're trying to access might have been moved or is no longer available."
          logoUrl={branding?.logoUrl}
        />
      </BannerContent>
    </GlobalView>
  );
};
